import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "grid w-full grid-cols-1 sm:grid-cols-2" }
const _hoisted_2 = { class: "bg-primary relative hidden w-full items-center justify-center sm:flex" }
const _hoisted_3 = ["alt"]
const _hoisted_4 = { class: "flex w-full flex-col items-center bg-gray-50 px-8 pt-20 sm:items-start sm:justify-center sm:pt-0 lg:px-28 xl:px-40" }
const _hoisted_5 = ["alt"]
const _hoisted_6 = { class: "mb-8 text-2xl font-semibold text-gray-700" }
const _hoisted_7 = { class: "mb-4 text-sm" }
const _hoisted_8 = { class: "mb-10 list-decimal pl-6 text-sm" }
const _hoisted_9 = { class: "flex flex-row-reverse text-sm" }
const _hoisted_10 = { class: "block" }

import { computed, ComputedRef, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';

import { useAuthStore, useUiStore } from '@/stores';
import { UserService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    required,
    maxLength,
    minLength,
    containsUppercase,
    containsLowercase,
    containsNumber,
    containsSpecial,
    sameAs,
} from '@/utils/validators';

import { UpdatePasswordFormState, UserModel } from '@/types';
import { AxiosResponse } from 'axios';

const imageLoginPath = '/img/slogan-login.png';
const logoPath = '/img/logomob.png';


export default /*@__PURE__*/_defineComponent({
  __name: 'UpdatePasswordPage',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();

const formState = reactive<UpdatePasswordFormState>({
    password: '',
    confirmPassword: '',
});

const token = computed(() => route.query.token as string | undefined);

const rules = computed<ValidationArgs<UpdatePasswordFormState>>(() => ({
    password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(250),
        containsUppercase,
        containsLowercase,
        containsNumber,
        containsSpecial,
    },
    confirmPassword: {
        required,
        sameAsPassword: sameAs(formState.password, t('user.labels.password')),
    },
}));

const v$ = useVuelidate(rules, formState);

const authStore = useAuthStore();
const user: ComputedRef<UserModel | undefined> = computed(() => authStore.userModel);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handlePasswordUpdate = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    try {
        setLoading(true);

        if (token.value) {
            const { data }: AxiosResponse = await UserService.resetPassword({
                token: token.value,
                password: formState.password,
            });

            if (data.success) {
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        } else {
            const { data }: AxiosResponse = await UserService.overwritePassword(formState);

            if (data.success) {
                localStorage.removeItem('tokenTemp');
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (!authStore.userNeedsUpdatePassword) {
        if (user.value !== undefined && user.value.requiresTwoFactor) {
            await router.push({ name: 'TwoFaAuthenticator' });
            return;
        } else if (token.value === null) {
            await router.push({ name: 'Home' });
        }
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiButton = _resolveComponent("UiButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("img", {
        src: imageLoginPath,
        class: "h-[150px] xl:h-[250px]",
        alt: _ctx.$t('authentication.logoAlt')
      }, null, 8, _hoisted_3)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("img", {
        class: "mb-8 w-[225px]",
        src: logoPath,
        alt: _ctx.$t('authentication.logoAlt')
      }, null, 8, _hoisted_5),
      _createElementVNode("h3", _hoisted_6, _toDisplayString(user.value?.isPasswordExpired
                        ? _ctx.$t('authentication.expiredPassword.title')
                        : _ctx.$t('authentication.updatePassword.title')), 1),
      _createElementVNode("p", _hoisted_7, _toDisplayString(user.value?.isPasswordExpired
                        ? _ctx.$t('authentication.expiredPassword.description')
                        : _ctx.$t('authentication.updatePassword.description')), 1),
      _createElementVNode("ul", _hoisted_8, [
        _createElementVNode("li", null, _toDisplayString(_ctx.$t('authentication.updatePassword.rule.size')), 1),
        _createElementVNode("li", null, _toDisplayString(_ctx.$t('authentication.updatePassword.rule.upperCase')), 1),
        _createElementVNode("li", null, _toDisplayString(_ctx.$t('authentication.updatePassword.rule.lowerCase')), 1),
        _createElementVNode("li", null, _toDisplayString(_ctx.$t('authentication.updatePassword.rule.number')), 1),
        _createElementVNode("li", null, _toDisplayString(_ctx.$t('authentication.updatePassword.rule.special')), 1)
      ]),
      _createElementVNode("form", {
        class: "w-full space-y-4",
        onSubmit: _withModifiers(handlePasswordUpdate, ["prevent"])
      }, [
        _createVNode(_component_UiTextInput, {
          modelValue: formState.password,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.password) = $event)),
          name: "password",
          type: "password",
          label: _ctx.$t('authentication.updatePassword.label.password'),
          inputClass: "text-base px-4 py-3",
          autocomplete: "password",
          errors: _unref(v$).password?.$errors,
          onBlur: _unref(v$).password?.$touch
        }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
        _createVNode(_component_UiTextInput, {
          modelValue: formState.confirmPassword,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.confirmPassword) = $event)),
          name: "confirmPassword",
          type: "password",
          label: _ctx.$t('authentication.updatePassword.label.confirmPassword'),
          inputClass: "text-base px-4 py-3",
          autocomplete: "off",
          errors: _unref(v$).confirmPassword?.$errors,
          onBlur: _unref(v$).confirmPassword?.$touch
        }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("span", {
            class: "text-primary hover:text-primary-darker cursor-pointer text-sm font-medium",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(router).push({ name: 'Login' })))
          }, _toDisplayString(_ctx.$t('core.actions.Back')), 1)
        ]),
        _createElementVNode("div", null, [
          _createVNode(_component_UiButton, {
            type: "submit",
            variant: "primary",
            class: "w-full"
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('login.form.signIn')), 1)
            ]),
            _: 1
          })
        ])
      ], 32)
    ])
  ]))
}
}

})